import React from "react";
import { Users, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TabsContent } from "@/components/ui/tabs";
import { formatTime12h, getEffectiveStartTime, calculateCallTime, getTodayString } from "../eventTime";
import { NotesDropdown } from "../NotesDropdown";
import { useTableControls, SortableTh, SearchBar, applyControls } from "../tableTools";

const SEARCH_FIELDS = [
  { value: "participant_name", label: "Crew Member" },
  { value: "participant_email", label: "Email" },
  { value: "event_name", label: "Event" },
  { value: "event_date", label: "Date" },
  { value: "status", label: "Status" },
];

const getValue = (r, field) => {
  switch (field) {
    case "participant_name": return r.participant_name;
    case "participant_email": return r.participant_email;
    case "event_name": return r.event_name;
    case "event_date": return r.event_date;
    case "start_time": return r.start_time;
    case "requested_at": return r.requested_at;
    case "status": return r.status;
    default: return "";
  }
};

export const AttendanceRequestsTab = (props) => {
  const { attendanceRequests, handleApproveAttendance, openWithdrawDialog } = props;
  const ctrl = useTableControls("event_date", "participant_name");
  // Clear requests from view after midnight of the event date (show only future-dated events)
  const today = getTodayString();
  const visibleRequests = (attendanceRequests || []).filter((r) => !r.event_date || r.event_date > today);
  const rows = applyControls(visibleRequests, ctrl, getValue);
  return (
          <TabsContent value="attendance-requests" data-testid="attendance-requests-content">
            <div className="bg-crew-zinc border border-zinc-800 rounded-sm overflow-hidden">
              <div className="p-4 border-b border-zinc-800 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                  <h2 className="text-xl font-bold text-crew-yellow">ATTENDANCE REQUESTS</h2>
                  <p className="text-zinc-400 text-sm">
                    {visibleRequests.filter(r => r.status === 'pending').length} pending • {visibleRequests.filter(r => r.status === 'approved').length} confirmed • {visibleRequests.filter(r => r.status === 'rejected').length} withdrawn
                  </p>
                </div>
                <SearchBar fields={SEARCH_FIELDS} testid="attendance-search" {...ctrl} />
              </div>

              {rows.length === 0 ? (
                <div className="p-12 text-center text-zinc-400">
                  <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>{visibleRequests.length === 0 ? "No upcoming attendance requests." : "No matching requests."}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-900">
                      <tr className="border-b border-zinc-700">
                        <SortableTh field="participant_name" label="Crew Member" {...ctrl} onSort={ctrl.handleSort} />
                        <SortableTh field="participant_email" label="Email" {...ctrl} onSort={ctrl.handleSort} />
                        <SortableTh field="event_name" label="Event" {...ctrl} onSort={ctrl.handleSort} />
                        <SortableTh field="event_date" label="Date" {...ctrl} onSort={ctrl.handleSort} />
                        <th className="py-3 px-4 text-left text-xs font-bold uppercase text-zinc-300">Crew Call</th>
                        <SortableTh field="start_time" label="Show Time" {...ctrl} onSort={ctrl.handleSort} />
                        <th className="py-3 px-4 text-left text-xs font-bold uppercase text-zinc-300">Notes</th>
                        <SortableTh field="requested_at" label="Requested" {...ctrl} onSort={ctrl.handleSort} />
                        <SortableTh field="status" label="Approved" {...ctrl} onSort={ctrl.handleSort} align="center" />
                        <th className="py-3 px-4 text-center text-xs font-bold uppercase text-zinc-300">Withdraw</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {rows.map((request, idx) => {
                        const effectiveStartTime = getEffectiveStartTime(request.start_time, request.notes);
                        const crewCallTime = calculateCallTime(request.start_time, request.call_time, request.notes);
                        return (
                        <tr 
                          key={request.id || idx} 
                          className={`hover:bg-zinc-900/50 ${request.status === 'pending' ? 'bg-yellow-900/10' : request.status === 'rejected' ? 'bg-red-900/10' : ''}`}
                        >
                          <td className="py-3 px-4 font-medium text-white">{request.participant_name}</td>
                          <td className="py-3 px-4 text-zinc-300">{request.participant_email}</td>
                          <td className="py-3 px-4 text-crew-yellow font-medium">{request.event_name}</td>
                          <td className="py-3 px-4 font-mono text-zinc-200">{request.event_date || '—'}</td>
                          <td className="py-3 px-4 font-mono text-crew-yellow font-bold">{formatTime12h(crewCallTime)}</td>
                          <td className="py-3 px-4 font-mono text-zinc-300">
                            {effectiveStartTime ? formatTime12h(effectiveStartTime) : '—'}
                          </td>
                          <td className="py-3 px-4">
                            <NotesDropdown notes={request.notes} />
                          </td>
                          <td className="py-3 px-4 text-zinc-400 text-xs">
                            {request.requested_at ? new Date(request.requested_at).toLocaleDateString() : '—'}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <input
                              type="checkbox"
                              checked={request.status === 'approved'}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  handleApproveAttendance(request.id);
                                } else {
                                  openWithdrawDialog(request);
                                }
                              }}
                              disabled={request.status === 'rejected'}
                              className={`w-5 h-5 cursor-pointer ${request.status === 'rejected' ? 'opacity-30 cursor-not-allowed' : ''}`}
                              style={{
                                accentColor: request.status === 'approved' ? '#22c55e' : '#eab308'
                              }}
                              data-testid={`approve-attendance-checkbox-${idx}`}
                              title={request.status === 'approved' ? 'Click to withdraw approval' : request.status === 'rejected' ? 'Withdrawn' : 'Click to approve'}
                            />
                          </td>
                          <td className="py-3 px-4 text-center">
                            {request.status === 'pending' && (
                              <Button
                                onClick={() => openWithdrawDialog(request)}
                                size="sm"
                                variant="outline"
                                className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                                data-testid={`withdraw-attendance-${idx}`}
                                title="Withdraw Request"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                            {request.status === 'rejected' && (
                              <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs font-bold rounded">WITHDRAWN</span>
                            )}
                          </td>
                        </tr>
                      );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>
  );
};
